// The Mountain orbit — shared by LIFE's two views. `useOrbit` owns the
// camera angle (drag, snap-to-face, D-pad turns, idle spin); `OrbitBackdrop`
// paints the current frame full-bleed and dissolves its flat sky into the
// page's starry night so the mountain sits in space, not in a box. The
// compass overlays this; the Mountain view overlays its paths and controls.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, PanResponder, StyleSheet, View, type GestureResponderHandlers } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Defs, RadialGradient, Stop, Mask, Rect, Image as SvgImage } from 'react-native-svg';
import type { Sector } from '../domain/olmps';
import { ORBIT_FRAMES, ORBIT_FRAMES_DAY } from './orbitFrames';
import { useV10 } from './theme';

const DAY_SKY = require('../../assets/v10/sky/day.jpg');
const NIGHT_SKY = require('../../assets/v10/sky/night.jpg');

/** Camera pitch of the Compass screen, as the disc's ry/rx: SIDE_PITCH is the
 *  normal side view of the Mountain, AERIAL_PITCH looks straight down on it. */
export const SIDE_PITCH = 0.3;
export const AERIAL_PITCH = 1;
export const PITCH_STEP = 0.175; // one ▲▼ press: four presses from side to top

export const SECTORS: Sector[] = ['S', 'H', 'A', 'P', 'E'];
const N = ORBIT_FRAMES.length;
export const FACE = 72;
const IDLE_DEG_PER_SEC = 5;
const DRAG_PX_PER_DEG = 2.2;

export interface Orbit {
  frame: number;
  frontDeg: number;
  frontSector: Sector;
  settled: boolean; // not turning, not spinning
  spinning: boolean;
  turn: (dir: 1 | -1) => void;
  snapToFace: (idx: number) => void;
  toggleSpin: () => void;
  panHandlers: GestureResponderHandlers;
}

export function useOrbit(startSector: Sector | null): Orbit {
  const startIdx = startSector ? SECTORS.indexOf(startSector) : 0;
  const deg = useRef(startIdx * FACE);
  const target = useRef<number | null>(null);
  const [frame, setFrame] = useState(Math.round((startIdx * FACE) / (360 / N)) % N);
  const [spinning, setSpinning] = useState(false);
  const [turning, setTurning] = useState(false);
  const spinRef = useRef(false);
  const dragging = useRef(false);
  const lastFrame = useRef(frame);

  const pushFrame = () => {
    const f = ((Math.round(deg.current / (360 / N)) % N) + N) % N;
    if (f !== lastFrame.current) {
      lastFrame.current = f;
      setFrame(f);
    }
  };
  useEffect(() => {
    let raf = 0;
    let prev = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - prev) / 1000);
      prev = now;
      if (!dragging.current) {
        if (target.current != null) {
          const diff = ((target.current - deg.current + 540) % 360) - 180;
          if (Math.abs(diff) < 0.4) {
            deg.current = target.current;
            target.current = null;
            setTurning(false);
          } else {
            deg.current = (deg.current + diff * Math.min(1, dt * 7) + 360) % 360;
          }
          pushFrame();
        } else if (spinRef.current) {
          deg.current = (deg.current + IDLE_DEG_PER_SEC * dt) % 360;
          pushFrame();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const snapToFace = (idx: number) => {
    target.current = ((idx % 5) + 5) % 5 * FACE;
    setTurning(true);
  };
  const frontIdxNow = () => ((Math.round(deg.current / FACE) % 5) + 5) % 5;
  const turn = (dir: 1 | -1) => {
    spinRef.current = false;
    setSpinning(false);
    const base = target.current != null ? Math.round(target.current / FACE) : frontIdxNow();
    snapToFace(base + dir);
  };
  const toggleSpin = () => {
    const on = !spinRef.current;
    spinRef.current = on;
    setSpinning(on);
    if (!on) snapToFace(frontIdxNow());
  };

  const startDeg = useRef(0);
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dx) > 6 && Math.abs(g.dx) > Math.abs(g.dy),
      onPanResponderGrant: () => {
        dragging.current = true;
        target.current = null;
        spinRef.current = false;
        setSpinning(false);
        setTurning(true);
        startDeg.current = deg.current;
      },
      onPanResponderMove: (_e, g) => {
        deg.current = (startDeg.current - g.dx / DRAG_PX_PER_DEG + 3600) % 360;
        pushFrame();
      },
      onPanResponderRelease: () => {
        dragging.current = false;
        snapToFace(frontIdxNow());
      },
      onPanResponderTerminate: () => {
        dragging.current = false;
        snapToFace(frontIdxNow());
      },
    }),
  ).current;

  const frontDeg = (frame * 360) / N;
  const frontSector = SECTORS[((Math.round(frontDeg / FACE) % 5) + 5) % 5];
  return { frame, frontDeg, frontSector, settled: !turning && !spinning, spinning, turn, snapToFace, toggleSpin, panHandlers: pan.panHandlers };
}

/** Soft white clouds for the Day sky (deterministic; replaces the stars). */
export function Clouds({ width, height }: { width: number; height: number }) {
  const puffs = useMemo(() => {
    const out: { x: number; y: number; w: number; h: number; o: number }[] = [];
    let seed = 11;
    const rnd = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    for (let i = 0; i < 7; i++) {
      const w = width * (0.28 + rnd() * 0.3);
      out.push({ x: rnd() * (width - w * 0.6) - w * 0.2, y: rnd() * height * 0.7, w, h: w * (0.22 + rnd() * 0.1), o: 0.35 + rnd() * 0.35 });
    }
    return out;
  }, [width, height]);
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      {puffs.map((c, i) => (
        <View key={i} style={{ position: 'absolute', left: c.x, top: c.y, width: c.w, height: c.h, opacity: c.o }}>
          <View style={{ position: 'absolute', left: 0, bottom: 0, width: c.w, height: c.h * 0.6, borderRadius: c.h, backgroundColor: '#ffffff' }} />
          <View style={{ position: 'absolute', left: c.w * 0.18, bottom: c.h * 0.25, width: c.w * 0.38, height: c.h * 0.75, borderRadius: c.h, backgroundColor: '#ffffff' }} />
          <View style={{ position: 'absolute', left: c.w * 0.45, bottom: c.h * 0.2, width: c.w * 0.32, height: c.h * 0.6, borderRadius: c.h, backgroundColor: '#ffffff' }} />
        </View>
      ))}
    </View>
  );
}

/** Day sky colours the graded frames were painted with (orbit_day_grade.py):
 *  the frame's top edge is DAY_TOP, its bottom edge DAY_BOT. */
const DAY_TOP = '#5aa9f0';
const DAY_MID = '#9fd0ff';
const DAY_BOT = '#e8f4ff';

/** The Mountain ONLY — an element that pans with the compass screen. The
 *  background (night starfield / day sky photo) is a separate fixed layer owned
 *  by the home, so this renders no sky, no stars and no page fill: the orbit
 *  frame is alpha-masked with a wide radial feather so its edges (and its baked
 *  sky, in both modes) dissolve into whatever constant sky sits behind. */
export function OrbitBackdrop({ frame, width, top, size, pitch, peakY }: { frame: number; width: number; height?: number; top: number; size: number; pitch?: Animated.Value; peakY?: number }) {
  const t = useV10();
  const frames = t.mode === 'dark' ? ORBIT_FRAMES : ORBIT_FRAMES_DAY;
  const left = (width - size) / 2;
  // As the camera rises over the summit the side face foreshortens about the
  // peak (cos of the pitch angle, normalised to the side view) and fades out;
  // the compass draws the aerial plate in its place.
  const tilt = useMemo(() => {
    if (!pitch) return null;
    const input = [SIDE_PITCH, 0.5, 0.65, 0.8, 0.9, AERIAL_PITCH];
    const face = (p: number) => Math.sqrt(1 - p * p) / Math.sqrt(1 - SIDE_PITCH * SIDE_PITCH);
    return {
      scaleY: pitch.interpolate({ inputRange: input, outputRange: input.map((p) => Math.max(0.02, face(p))), extrapolate: 'clamp' }),
      opacity: pitch.interpolate({ inputRange: [0.5, 0.85], outputRange: [1, 0], extrapolate: 'clamp' }),
    };
  }, [pitch]);
  const pivot = peakY ?? top + size / 2;
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      {/* Centred on the peak (height 2 × pivot), so scaleY pivots there. */}
      <Animated.View style={[{ position: 'absolute', left: 0, right: 0, top: 0, height: pivot * 2 }, tilt && { opacity: tilt.opacity, transform: [{ scaleY: tilt.scaleY }] }]}>
        {/* Frames are cut out (art-masters/v10/orbit_cutout.py): no sky, no mask. */}
        {frames.map((src, i) => (
          <Image key={i} source={src} style={{ position: 'absolute', left, top, width: size, height: size, opacity: i === frame ? 1 : 0 }} resizeMode="contain" />
        ))}
      </Animated.View>
    </View>
  );
}

/** Starfield (Night) / sunny sky with soft clouds (Day) for the screens below
 *  the Mountain (ORB, SELF, tools). Its top edge is the page colour the
 *  Mountain screen ends in, so descending reads as one continuous world. */
export function NightBackdrop({ width, height }: { width: number; height: number }) {
  const t = useV10();
  const dark = t.mode === 'dark';
  if (!dark) return <DayBackdrop width={width} height={height} />;
  // A calm, deep night with a faint Milky Way (scripts/build_night_sky.py) —
  // the page navy at top and bottom, so it still meets the Mountain seamlessly.
  return (
    <View pointerEvents="none" style={[StyleSheet.absoluteFill, { backgroundColor: '#070b17' }]}>
      <Image source={NIGHT_SKY} style={{ position: 'absolute', left: 0, top: 0, width, height }} resizeMode="cover" />
      <LinearGradient colors={['rgba(214,180,106,0.10)', 'rgba(214,180,106,0)']} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: height * 0.35 }} />
    </View>
  );
}

/** Day variant: clear sky blue, clouds, a faint sun-warm glow at the top. */
export function DayBackdrop({ width, height }: { width: number; height: number }) {
  // A real photographed sky (art-masters/v10/sky, logged in ART_LOG) — no drawn clouds.
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Image source={DAY_SKY} style={{ position: 'absolute', left: 0, top: 0, width, height }} resizeMode="cover" />
    </View>
  );
}
